import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Plus, Check, X, Volume2, ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { CefrBadge } from '@/components/CefrBadge'
import { understandApi } from '@/api/understand'
import { interactionsApi, type EngagementType } from '@/api/interactions'
import { vocabularyApi } from '@/api/vocabulary'
import type { CefrLevel } from '@/types/common'
import type { LocalizedWordDefinition, UnderstandResponse } from '@/types/content'
import type { UserWordDetail } from '@/types/user'
import { cn } from '@/components/ui/cn'
import { computePopupPlacement } from './wordPopupPlacement'
import { PopupIssueReportButton, PopupIssueReportPanel } from './WordPopupIssueReport'
import { copy } from '@/lib/copy'
import { phraseLimitExceededMessage } from '@/components/ai/AiCreditsIndicator'
import { isSenseLearned } from '@/lib/senseUtils'
import { playPronunciation } from '@/lib/playPronunciation'
import { normalizeEnglishWord } from '@/lib/wordValidation'
import { useMobileViewport } from '@/hooks/useMobileViewport'

interface WordPopupBaseProps {
  selection: string
  context: string
  anchorRect: DOMRect
  onClose: () => void
  onWordAdded?: (word: string) => void
  /** ADR-017 span flash — fires once when a multi-word unit is the silent primary. */
  onSpanFlash?: (phraseSurface: string) => void
}

type WordPopupProps = WordPopupBaseProps & (
  | { mode: 'article'; articleId: string }
  | { mode: 'practice' }
  | { mode: 'demo'; demoUnderstand: UnderstandResponse }
)

const NATIVE_LANGUAGE_NAMES: Record<string, string> = {
  ru: 'Russian',
  pl: 'Polish',
  de: 'German',
  fr: 'French',
  es: 'Spanish',
  uk: 'Ukrainian',
}

const UNDERSTANDING_DWELL_MS = 3000
const SHEET_DISMISS_DRAG_PX = 60

function SheetDragHandle({ onClose }: { onClose: () => void }) {
  const touchStartY = useRef<number | null>(null)

  return (
    <div
      className="flex shrink-0 flex-col items-center border-b border-border px-4 pb-2 pt-3"
      data-testid="word-popup-drag-handle"
      onTouchStart={(event) => {
        touchStartY.current = event.touches[0]?.clientY ?? null
      }}
      onTouchEnd={(event) => {
        if (touchStartY.current == null) return
        const endY = event.changedTouches[0]?.clientY
        if (endY != null && endY - touchStartY.current > SHEET_DISMISS_DRAG_PX) {
          onClose()
        }
        touchStartY.current = null
      }}
    >
      <div className="mb-2 h-1 w-10 rounded-full bg-muted-foreground/35" aria-hidden />
    </div>
  )
}

export function WordPopup(props: WordPopupProps) {
  const { selection, context, anchorRect, onClose, onWordAdded, onSpanFlash } = props
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const isMobileSheet = useMobileViewport()
  const [otherExpanded, setOtherExpanded] = useState(false)
  const [selectedSenseKey, setSelectedSenseKey] = useState<string | null>(null)
  const [justAdded, setJustAdded] = useState(false)
  const [grammarEpisodeSaved, setGrammarEpisodeSaved] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)
  const [reportOpen, setReportOpen] = useState(false)
  const understandingRecorded = useRef(false)
  const spanFlashSent = useRef(false)
  const phraseShownSent = useRef(false)
  const [chooserPick, setChooserPick] = useState<'phrase' | 'word' | null>(null)
  const clientSessionId = useMemo(() => crypto.randomUUID(), [selection, context])

  const isDemo = props.mode === 'demo'
  const isArticle = props.mode === 'article'
  const articleId = isArticle ? props.articleId : undefined
  const understandQueryKey = isArticle
    ? ['understand', 'article', articleId, selection, context] as const
    : ['understand', 'practice', selection, context] as const

  const { data: understandFromApi, isLoading, error: understandError } = useQuery({
    queryKey: understandQueryKey,
    queryFn: () =>
      understandApi.understand({
        selection,
        context,
        sourceType: isArticle ? 'ARTICLE' : 'AI_PRACTICE',
        articleId: isArticle ? articleId : undefined,
      }).then((r) => r.data),
    enabled: !isDemo,
    staleTime: 1000 * 60 * 5,
    retry: (failureCount, err) => {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 429) return false
      return failureCount < 1
    },
  })

  const understand = isDemo ? props.demoUnderstand : understandFromApi

  const understandErrorMessage =
    (understandError as { response?: { status?: number; data?: { message?: string } } })?.response?.status === 429
      ? phraseLimitExceededMessage(
          (understandError as { response?: { data?: { message?: string } } })?.response?.data?.message,
        )
      : null

  const popupRef = useRef<HTMLDivElement>(null)
  const [popupHeight, setPopupHeight] = useState<number | undefined>()
  const [viewportVersion, setViewportVersion] = useState(0)

  useLayoutEffect(() => {
    if (isMobileSheet) return
    const el = popupRef.current
    if (!el) return

    const updateHeight = () => {
      setPopupHeight(el.getBoundingClientRect().height)
    }

    updateHeight()
    const observer = new ResizeObserver(updateHeight)
    observer.observe(el)
    return () => observer.disconnect()
  }, [isMobileSheet, understand, isLoading, otherExpanded, selectedSenseKey, addError, justAdded, reportOpen])

  useEffect(() => {
    if (isMobileSheet) return
    const onViewportChange = () => setViewportVersion((value) => value + 1)
    window.addEventListener('resize', onViewportChange)
    window.addEventListener('scroll', onViewportChange, true)
    return () => {
      window.removeEventListener('resize', onViewportChange)
      window.removeEventListener('scroll', onViewportChange, true)
    }
  }, [isMobileSheet])

  // Lock the page behind the bottom sheet so dragging the popup's non-scrolling
  // areas can't scroll the article underneath it.
  useEffect(() => {
    if (!isMobileSheet) return
    const { body } = document
    const previousOverflow = body.style.overflow
    const previousOverscroll = body.style.overscrollBehavior
    body.style.overflow = 'hidden'
    body.style.overscrollBehavior = 'contain'
    return () => {
      body.style.overflow = previousOverflow
      body.style.overscrollBehavior = previousOverscroll
    }
  }, [isMobileSheet])

  const placement = useMemo(
    () => (isMobileSheet ? null : computePopupPlacement(anchorRect, { popupHeight })),
    [anchorRect, isMobileSheet, popupHeight, viewportVersion],
  )

  const isPhraseResponse = understand?.kind === 'PHRASE'
  const phraseQuotaExceeded = understand?.phraseQuotaExceeded === true
  const nestedWordConstituent = understand?.constituents?.find(
    (c) => c.kind === 'WORD' && c.wordDetails,
  )
  const nestedWordPopup = nestedWordConstituent?.wordDetails ?? null
  const nestedWordSurface = (nestedWordConstituent?.selection ?? '').trim().toLowerCase()
  const phraseSurfaceKey = (understand?.selection ?? '').trim().toLowerCase()
  /** CHOOSER only when the word option is a real, different surface — otherwise empty/duplicate UX. */
  const canOfferChooser =
    Boolean(nestedWordConstituent && nestedWordPopup)
    && nestedWordSurface.length > 0
    && nestedWordSurface !== phraseSurfaceKey
  const awaitingChooser =
    isPhraseResponse
    && understand?.match?.presentation === 'CHOOSER'
    && canOfferChooser
    && chooserPick == null
  /** Band B: "Just the word" must show nested WORD as a normal word popup (not an empty sheet). */
  const showingChooserWord =
    isPhraseResponse
    && chooserPick === 'word'
    && canOfferChooser
  const showPhraseBody =
    isPhraseResponse
    && !phraseQuotaExceeded
    && !awaitingChooser
    && !showingChooserWord
  const showNestedWord =
    Boolean(nestedWordConstituent && nestedWordPopup)
    && isPhraseResponse
    && !phraseQuotaExceeded
    && !awaitingChooser
    && !showingChooserWord
  const isPhrase = isPhraseResponse && !showingChooserWord
  const popup = showingChooserWord
    ? nestedWordPopup
    : (understand?.wordDetails ?? null)
  const phraseUnitTypeLabel = isPhrase && understand?.unitType && understand.unitType !== 'WORD'
    ? understand.unitType.replaceAll('_', ' ').toLowerCase()
    : null
  const unitTypeLabel = popup?.unitType && popup.unitType !== 'WORD'
    ? popup.unitType.replaceAll('_', ' ').toLowerCase()
    : null

  const recordEngagement = useCallback((engagementType: EngagementType) => {
    if (isDemo || understandingRecorded.current || isLoading || !understand) {
      return
    }
    understandingRecorded.current = true
    void interactionsApi.record({
      selection,
      context,
      sourceType: isArticle ? 'ARTICLE' : 'AI_PRACTICE',
      articleId: isArticle ? articleId : undefined,
      engagementType,
      clientSessionId,
    })
  }, [articleId, clientSessionId, context, isArticle, isDemo, isLoading, understand, selection])

  const recordPhraseEvent = useCallback((engagementType: 'PHRASE_POPUP_SHOWN' | 'PHRASE_ADD_INTENT') => {
    if (isDemo || isLoading || !understand || understand.kind !== 'PHRASE') {
      return
    }
    void interactionsApi.record({
      selection: understand.selection,
      context,
      sourceType: isArticle ? 'ARTICLE' : 'AI_PRACTICE',
      articleId: isArticle ? articleId : undefined,
      engagementType,
      // Fresh session id — unique index is per popup session for DWELL.
      clientSessionId: crypto.randomUUID(),
    })
  }, [articleId, context, isArticle, isDemo, isLoading, understand])

  useEffect(() => {
    understandingRecorded.current = false
    spanFlashSent.current = false
    phraseShownSent.current = false
    setChooserPick(null)
  }, [selection, context, clientSessionId])

  useEffect(() => {
    if (!understand || isLoading || phraseShownSent.current) {
      return
    }
    if (understand.kind !== 'PHRASE' || understand.phraseQuotaExceeded) {
      return
    }
    phraseShownSent.current = true
    recordPhraseEvent('PHRASE_POPUP_SHOWN')
  }, [understand, isLoading, recordPhraseEvent])

  useEffect(() => {
    if (!understand || isLoading || spanFlashSent.current || !onSpanFlash) {
      return
    }
    const multiWord =
      understand.kind === 'PHRASE'
      && understand.selection.includes(' ')
      && (understand.match?.presentation === 'SILENT_PRIMARY'
        || understand.match?.presentation === 'CHOOSER'
        || !understand.match)
    if (!multiWord) {
      return
    }
    const spanLen =
      understand.match?.startToken != null && understand.match?.endToken != null
        ? understand.match.endToken - understand.match.startToken + 1
        : understand.selection.trim().split(/\s+/).length
    if (spanLen < 2) {
      return
    }
    spanFlashSent.current = true
    onSpanFlash(understand.selection)
  }, [understand, isLoading, onSpanFlash])

  useEffect(() => {
    if (!understand || isLoading) {
      return
    }
    if (understand.kind === 'PHRASE') {
      return // PHRASE_POPUP_SHOWN already covers exposure
    }
    const timer = window.setTimeout(() => recordEngagement('DWELL'), UNDERSTANDING_DWELL_MS)
    return () => window.clearTimeout(timer)
  }, [understand, isLoading, recordEngagement])

  const handleOtherMeaningsToggle = () => {
    setOtherExpanded((v) => {
      const next = !v
      if (next) {
        recordEngagement('SENSE_EXPLORED')
      }
      return next
    })
  }

  const handlePlayAudio = (text: string, audioUrl?: string | null) => {
    recordEngagement('AUDIO_PLAYED')
    playPronunciation(text, audioUrl)
  }

  const primarySense = popup?.localizedDefinitions.find((d) => d.isPrimary)
    ?? popup?.localizedDefinitions[0]

  const sensesByFrequency = useMemo(() => {
    const definitions = popup?.localizedDefinitions ?? []
    const primary = definitions.filter((d) => d.isPrimary)
    const rest = definitions.filter((d) => !d.isPrimary)
    return [...primary, ...rest]
  }, [popup?.localizedDefinitions])

  const isInflectedMode = popup?.popupMode === 'INFLECTED'

  const greenZoneTranslations = useMemo(() => {
    const seen = new Set<string>()
    const unique: string[] = []
    for (const sense of sensesByFrequency) {
      const translation = sense.translation?.trim()
      if (!translation) continue
      const key = translation.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      unique.push(translation)
      if (unique.length >= 3) break
    }
    return unique
  }, [sensesByFrequency])

  const displaySurface = popup?.surfaceForm ?? understand?.selection ?? ''
  const lemma = popup?.lemma ?? popup?.word
  const posLabel = isInflectedMode
    ? (popup?.resolvedPartOfSpeech ?? primarySense?.partOfSpeech)
    : primarySense?.partOfSpeech
  const lemmaTeachingText = popup?.lemmaTeachingTranslation ?? popup?.primaryTranslation ?? null
  const isGrammarPattern =
    understand?.unitType === 'GRAMMAR_PATTERN' && !showingChooserWord
  const composedGrammarHero = isGrammarPattern
    ? understand?.composedHero?.trim() || null
    : null
  const grammarNestedL1 = isGrammarPattern
    ? (nestedWordConstituent?.headline?.trim()
      || nestedWordPopup?.primaryTranslation?.trim()
      || null)
    : null
  /** L1 meaning for Episode save — never English pedagogy title (headline). */
  const grammarEpisodeHero = isGrammarPattern
    ? (composedGrammarHero || grammarNestedL1 || understand?.explanation?.trim() || null)
    : null
  const formDescriptionInHeader = Boolean(popup?.formDescription)
  const showLemmaSubtitle = !isPhrase && Boolean(lemma)
    && (
      displaySurface.toLowerCase() !== lemma!.toLowerCase()
      || formDescriptionInHeader
      || isInflectedMode
    )
  // formInsight already covers EN form label — drop formDescription to avoid a tall stacked header.
  const hasFormInsight = Boolean(popup?.formInsight)
  const formMetaLine = [
    hasFormInsight ? null : popup?.formDescription,
    isInflectedMode && !hasFormInsight ? popup?.localizedGrammar : null,
  ].filter(Boolean).join(' · ')

  useEffect(() => {
    if (primarySense && selectedSenseKey === null) {
      setSelectedSenseKey(primarySense.senseKey)
    }
  }, [primarySense, selectedSenseKey])

  const selectedSense = popup?.localizedDefinitions.find((d) => d.senseKey === selectedSenseKey)
    ?? primarySense

  const inflectedFormTranslation = isInflectedMode
    ? (popup?.displayTranslation ?? null)
    : null

  const heroTranslation = isInflectedMode
    ? inflectedFormTranslation
    : selectedSense?.translation

  const showTranslationHero = isInflectedMode
    ? Boolean(inflectedFormTranslation)
    : Boolean(selectedSense)

  const alsoReadingTranslation =
    isInflectedMode && popup?.alsoTranslation
      ? popup.alsoTranslation
      : null

  const learnedSenses = popup?.learnedSenses ?? []
  const inVocabulary = popup?.inVocabulary ?? learnedSenses.length > 0
  const selectedLearned = selectedSense
    ? isSenseLearned(selectedSense.senseKey, selectedSense.translation, learnedSenses)
    : false
  const primaryLearned = primarySense
    ? isSenseLearned(primarySense.senseKey, primarySense.translation, learnedSenses)
    : false
  const enrichmentFailed = popup?.enrichmentStatus === 'FAILED'
  const enrichmentPartial = popup?.enrichmentStatus === 'PARTIAL'

  const addWordMutation = useMutation({
    mutationFn: () => {
      const addingNested = Boolean(isPhrase && nestedWordConstituent && nestedWordPopup)
      const wordToAdd = normalizeEnglishWord(
        addingNested
          ? nestedWordConstituent!.selection
          : (popup?.surfaceForm ?? selection),
      )
      const nestedPrimary = nestedWordPopup?.localizedDefinitions.find((d) => d.isPrimary)
        ?? nestedWordPopup?.localizedDefinitions[0]
      return vocabularyApi.addWord({
        word: wordToAdd,
        translation: addingNested
          ? (nestedPrimary?.translation || nestedWordConstituent?.headline || nestedWordPopup?.primaryTranslation || undefined)
          : (selectedSense?.translation || popup?.primaryTranslation || undefined),
        senseKey: addingNested ? nestedPrimary?.senseKey : selectedSense?.senseKey,
        source: isArticle ? 'ARTICLE' : 'AI',
        sourceReference: isArticle ? articleId : undefined,
        contextSentence: context,
      }).then((r) => r.data)
    },
    onSuccess: async (saved: UserWordDetail) => {
      setJustAdded(true)
      setAddError(null)
      queryClient.setQueryData<UnderstandResponse>(understandQueryKey, (old) => {
        if (!old) return old
        const addingNested = old.kind === 'PHRASE'
        if (addingNested && old.constituents?.length) {
          return {
            ...old,
            constituents: old.constituents.map((c) => {
              if (c.kind !== 'WORD' || !c.wordDetails) return c
              const nestedPrimary = c.wordDetails.localizedDefinitions.find((d) => d.isPrimary)
                ?? c.wordDetails.localizedDefinitions[0]
              const learned = {
                senseKey: saved.senseKey ?? nestedPrimary?.senseKey,
                translation: nestedPrimary?.translation ?? saved.translation,
              }
              const learnedSenses = [...c.wordDetails.learnedSenses]
              if (!isSenseLearned(learned.senseKey, learned.translation, learnedSenses)) {
                learnedSenses.push(learned)
              }
              return {
                ...c,
                wordDetails: {
                  ...c.wordDetails,
                  inVocabulary: true,
                  vocabularyWordId: saved.id,
                  userTranslation: saved.translation,
                  learnedSenses,
                },
              }
            }),
          }
        }
        if (!old.wordDetails) return old
        const learned = {
          senseKey: saved.senseKey ?? selectedSense?.senseKey,
          translation: selectedSense?.translation ?? saved.translation,
        }
        const learnedSenses = [...old.wordDetails.learnedSenses]
        if (!isSenseLearned(learned.senseKey, learned.translation, learnedSenses)) {
          learnedSenses.push(learned)
        }
        return {
          ...old,
          wordDetails: {
            ...old.wordDetails,
            inVocabulary: true,
            vocabularyWordId: saved.id,
            userTranslation: saved.translation,
            learnedSenses,
          },
        }
      })
      if (isArticle) {
        queryClient.invalidateQueries({ queryKey: ['article', articleId] })
      }
      await queryClient.refetchQueries({ queryKey: understandQueryKey })
      queryClient.invalidateQueries({ queryKey: ['vocabulary'] })
      queryClient.invalidateQueries({ queryKey: ['vocabularyStats'] })
      queryClient.invalidateQueries({ queryKey: ['exploreSession'] })

      // Article/practice "in vocabulary" underlines come from knownForms — keep them fresh.
      const surfaceAdded = normalizeEnglishWord(
        (isPhrase || showingChooserWord) && nestedWordConstituent
          ? nestedWordConstituent.selection
          : (popup?.surfaceForm ?? selection),
      )
      queryClient.setQueryData<string[]>(['knownForms'], (old) => {
        const next = new Set((old ?? []).map((f) => f.toLowerCase()))
        next.add(saved.word.toLowerCase())
        next.add(surfaceAdded.toLowerCase())
        for (const form of saved.forms ?? []) {
          next.add(form.toLowerCase())
        }
        return [...next]
      })
      void queryClient.invalidateQueries({ queryKey: ['knownForms'] })

      onWordAdded?.(
        (isPhrase || showingChooserWord) && nestedWordConstituent
          ? nestedWordConstituent.selection
          : (lemma ?? selection),
      )
      // Also notify with the exact surface from the text (lemma may differ: economy vs economies).
      if (surfaceAdded.toLowerCase() !== (lemma ?? selection).toLowerCase()) {
        onWordAdded?.(surfaceAdded)
      }
    },
    onError: (err: unknown) => {
      const response = (err as { response?: { status: number; data?: { message?: string } } })?.response
      if (response?.status === 409) {
        setJustAdded(true)
        setAddError(null)
        void queryClient.refetchQueries({ queryKey: understandQueryKey })
        void queryClient.invalidateQueries({ queryKey: ['knownForms'] })
        return
      }
      setAddError(response?.data?.message ?? copy.vocabulary.addWordFailed)
    },
  })

  const [phraseSaved, setPhraseSaved] = useState(false)

  const nestedWordSaved = Boolean(
    nestedWordPopup?.inVocabulary
      || (nestedWordPopup?.learnedSenses?.length ?? 0) > 0
      || (justAdded && isPhrase && nestedWordConstituent),
  )

  const addPhraseMutation = useMutation({
    mutationFn: () => {
      if (!understand?.phraseId && !understand?.selection) {
        return Promise.reject(new Error('No catalog phrase'))
      }
      return vocabularyApi.addPhrase({
        phraseId: understand.phraseId,
        normalizedPhrase: understand.selection,
        translation: understand.headline ?? undefined,
        source: isArticle ? 'ARTICLE' : 'AI',
        sourceReference: isArticle ? articleId : undefined,
      }).then((r) => r.data)
    },
    onSuccess: () => {
      setPhraseSaved(true)
      setAddError(null)
      recordPhraseEvent('PHRASE_ADD_INTENT')
      void queryClient.invalidateQueries({ queryKey: ['vocabulary'] })
      void queryClient.invalidateQueries({ queryKey: ['vocabularyPhrases'] })
      void queryClient.invalidateQueries({ queryKey: ['knownPhrases'] })
      void queryClient.invalidateQueries({ queryKey: ['vocabularyStats'] })
      void queryClient.invalidateQueries({ queryKey: understandQueryKey })
    },
    onError: (err: unknown) => {
      const response = (err as { response?: { status: number; data?: { message?: string } } })?.response
      if (response?.status === 409) {
        setPhraseSaved(true)
        setAddError(null)
        return
      }
      setAddError(response?.data?.message ?? copy.word.savePhraseFailed)
    },
  })

  const saveGrammarEpisodeMutation = useMutation({
    mutationFn: () => {
      if (!understand?.selection) {
        return Promise.reject(new Error('No grammar span'))
      }
      if (!grammarEpisodeHero) {
        return Promise.reject(new Error('No L1 meaning to save'))
      }
      return understandApi.saveGrammarEpisode({
        span: understand.selection,
        sentence: context || undefined,
        heroL1: grammarEpisodeHero,
        headline: understand.headline || undefined,
        formRecipe: understand.literalTranslation || undefined,
        nestedSurface: nestedWordConstituent?.selection || undefined,
        nestedL1: grammarNestedL1 || undefined,
        confidence: understand.match?.confidence,
        articleId: isArticle ? articleId : undefined,
      }).then((r) => r.data)
    },
    onSuccess: () => {
      setGrammarEpisodeSaved(true)
      setAddError(null)
      void queryClient.invalidateQueries({ queryKey: understandQueryKey })
    },
    onError: (err: unknown) => {
      const response = (err as { response?: { status: number; data?: { message?: string } } })?.response
      setAddError(response?.data?.message ?? copy.word.saveGrammarEpisodeFailed)
    },
  })

  // Reset save UI only when the user opens a different span — not when inVocabulary
  // flips after a successful save (that used to flash "meet in review" → "Already learning").
  useEffect(() => {
    setPhraseSaved(Boolean(understand?.inVocabulary))
    setAddError(null)
    setJustAdded(false)
    setGrammarEpisodeSaved(Boolean(understand?.episodeSaved))
    setReportOpen(false)
  }, [understand?.phraseId, understand?.selection, understand?.kind, understand?.unitType])

  useEffect(() => {
    if (understand?.inVocabulary) {
      setPhraseSaved(true)
    }
    if (understand?.episodeSaved) {
      setGrammarEpisodeSaved(true)
    }
  }, [understand?.inVocabulary, understand?.episodeSaved])

  const canAddSelectedSense = !isPhrase && popup && selectedSense && !selectedLearned && !enrichmentFailed && !addWordMutation.isPending
  const canAddNestedWord = Boolean(
    isPhrase
    && isGrammarPattern
    && showNestedWord
    && nestedWordPopup
    && !nestedWordSaved
    && nestedWordPopup.enrichmentStatus !== 'FAILED'
    && !addWordMutation.isPending,
  )
  const showSavedFooter = justAdded || (inVocabulary && selectedLearned)
  const showGrammarNestedSavedFooter = Boolean(
    isPhrase && isGrammarPattern && nestedWordSaved,
  )
  const showWordFooter = Boolean(
    understand
    && ((!isPhrase && popup) || isGrammarPattern),
  )
  const phraseExampleLimit = isMobileSheet ? 1 : 2

  useEffect(() => {
    if (justAdded) {
      setJustAdded(false)
    }
  }, [selectedSenseKey])

  const canAdd = canAddSelectedSense
  const showEnglishDefinitions = popup?.translationLocalized && popup.definitions.length > 0
  const nativeLanguageCode =
    popup?.nativeLanguage
    ?? nestedWordPopup?.nativeLanguage
    ?? null
  const nativeLabel = nativeLanguageCode
    ? NATIVE_LANGUAGE_NAMES[nativeLanguageCode] ?? nativeLanguageCode.toUpperCase()
    : 'Translation'

  const primarySenses = sensesByFrequency.filter((d) => d.isPrimary)
  const secondarySenses = sensesByFrequency.filter((d) => !d.isPrimary)
  const isPolysemous = sensesByFrequency.length > 1
  const collapseSecondary = secondarySenses.length > 4
  const visibleSecondary = collapseSecondary && !otherExpanded
    ? secondarySenses.filter((d) => d.senseKey === selectedSenseKey)
    : secondarySenses
  const hiddenSecondaryCount = secondarySenses.length - visibleSecondary.length

  const popupStyle: React.CSSProperties | undefined = placement
    ? {
        left: placement.left,
        width: placement.width,
        maxHeight: placement.maxHeight,
        ...(placement.top != null ? { top: placement.top } : { bottom: placement.bottom }),
      }
    : undefined

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-40',
          isMobileSheet ? 'touch-none bg-black/40' : 'bg-black/20',
        )}
        onClick={onClose}
        data-testid="word-popup-backdrop"
      />

      <div
        ref={popupRef}
        className={cn(
          'fixed z-50 flex flex-col overflow-hidden border border-border bg-card shadow-xl',
          isMobileSheet
            ? 'inset-x-0 bottom-0 max-h-[70dvh] rounded-t-2xl'
            : 'rounded-xl',
        )}
        style={popupStyle}
        data-testid="word-popup"
        data-mobile-sheet={isMobileSheet ? 'true' : 'false'}
        data-placement={placement?.placement}
      >
        {isMobileSheet ? <SheetDragHandle onClose={onClose} /> : null}

        <div
          className={cn(
            'relative shrink-0 border-b border-border px-4 pb-1.5',
            isMobileSheet ? 'pt-2' : 'pt-3',
          )}
        >
          <div
            className={cn(
              'absolute right-2 top-2 z-10 flex items-center',
              isMobileSheet ? 'gap-0' : 'gap-0.5',
            )}
          >
            {!isDemo ? (
              <PopupIssueReportButton
                open={reportOpen}
                onOpenChange={setReportOpen}
                isMobileSheet={isMobileSheet}
              />
            ) : null}
            <button
              onClick={onClose}
              className={cn(
                'text-muted-foreground hover:text-foreground',
                isMobileSheet && 'flex h-11 w-11 items-center justify-center rounded-md',
                !isMobileSheet && 'rounded-md p-1',
              )}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {isLoading ? (
            <div className="space-y-2 pr-16">
              <div className="h-5 w-24 animate-pulse rounded bg-muted" />
              <div className="h-3 w-16 animate-pulse rounded bg-muted" />
            </div>
          ) : understandErrorMessage ? (
            <p className="pr-16 text-sm text-destructive" data-testid="popup-understand-error">
              {understandErrorMessage}
            </p>
          ) : understand ? (
            <div className="pr-16">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-lg font-semibold text-foreground" data-testid="popup-surface-form">
                  {displaySurface}
                </span>
                {!isPhrase && unitTypeLabel && (
                  <span className="rounded-md border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {unitTypeLabel}
                  </span>
                )}
                {!isPhrase && popup?.phonetic && (
                  <span className="text-xs text-muted-foreground">{popup.phonetic}</span>
                )}
                {!isPhrase && displaySurface && (
                  <button
                    type="button"
                    onClick={() => handlePlayAudio(displaySurface, popup?.audioUrl)}
                    className={cn(
                      'rounded-md text-muted-foreground hover:text-foreground',
                      isMobileSheet
                        ? 'flex h-11 w-11 items-center justify-center'
                        : 'p-0.5',
                    )}
                    aria-label="Play pronunciation"
                    data-testid="popup-play-audio"
                  >
                    <Volume2 className="h-4 w-4" />
                  </button>
                )}
                {!isPhrase && popup?.cefrLevel && !enrichmentPartial && (
                  <CefrBadge level={popup.cefrLevel as CefrLevel} />
                )}
              </div>
              {formMetaLine ? (
                <p
                  className="mt-0.5 text-xs leading-snug text-muted-foreground"
                  data-testid="popup-form-description"
                >
                  {formMetaLine}
                </p>
              ) : null}
              {showLemmaSubtitle && (
                <p className="mt-0.5 text-xs leading-snug text-muted-foreground" data-testid="popup-lemma-subtitle">
                  ↓ {lemma}
                  {posLabel ? ` · ${posLabel}` : ''}
                  {popup?.morphHint ? ` · ${popup.morphHint}` : ''}
                  {isInflectedMode && lemmaTeachingText ? ` · ${lemmaTeachingText}` : ''}
                </p>
              )}
              {isPhrase && isGrammarPattern ? (
                understand.headline ? (
                  <p
                    className="mt-0.5 text-sm font-medium text-muted-foreground"
                    data-testid="popup-grammar-headline"
                  >
                    {understand.headline}
                  </p>
                ) : null
              ) : isPhrase ? (
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <p className="text-xs text-muted-foreground">{copy.word.expression}</p>
                  {phraseUnitTypeLabel && (
                    <span
                      className="rounded-md border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                      data-testid="popup-phrase-unit-type"
                    >
                      {phraseUnitTypeLabel}
                    </span>
                  )}
                  {understand.inCatalog && (
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      Catalog
                    </span>
                  )}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {!isDemo ? (
          <PopupIssueReportPanel
            open={reportOpen}
            onOpenChange={setReportOpen}
            context={{
              selection,
              contextSnippet: context,
              kind: understand?.kind,
              unitType: understand?.unitType ?? popup?.unitType,
              lemma: popup?.lemma ?? popup?.word,
              surface: displaySurface,
              headline: understand?.headline,
              translation:
                popup?.displayTranslation
                ?? popup?.readingTranslation
                ?? popup?.teachingTranslation
                ?? popup?.primaryTranslation,
              wordFormId: popup?.wordFormId,
              articleId,
            }}
          />
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 pt-3">
          {isLoading ? (
            <div className="space-y-2">
              <div className="h-8 w-full animate-pulse rounded bg-muted" />
              <div className="h-3 w-full animate-pulse rounded bg-muted" />
              <div className="h-3 w-4/5 animate-pulse rounded bg-muted" />
            </div>
          ) : understandErrorMessage ? null : understand ? (
            <>
              {isPhrase ? (
                phraseQuotaExceeded ? null : (
                <>
                  {awaitingChooser && (
                    <div
                      className="mb-3 rounded-lg border border-border bg-muted/40 p-3"
                      data-testid="popup-unit-chooser"
                    >
                      <p className="mb-2 text-xs font-medium text-foreground">
                        {copy.word.chooserTitle}
                      </p>
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <button
                          type="button"
                          className="flex-1 rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-left text-sm font-medium text-foreground hover:bg-primary/15"
                          onClick={() => setChooserPick('phrase')}
                          data-testid="popup-chooser-phrase"
                        >
                          <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">
                            {understand.unitType === 'GRAMMAR_PATTERN'
                              ? copy.word.chooserPattern
                              : copy.word.chooserPhrase}
                          </span>
                          {understand.selection}
                        </button>
                        <button
                          type="button"
                          className="flex-1 rounded-md border border-border bg-card px-3 py-2 text-left text-sm font-medium text-foreground hover:bg-muted/60"
                          onClick={() => setChooserPick('word')}
                          data-testid="popup-chooser-word"
                        >
                          <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">
                            {copy.word.chooserWord}
                          </span>
                          {nestedWordConstituent!.selection}
                        </button>
                      </div>
                    </div>
                  )}

                  {showPhraseBody && (
                    <>
                  {isGrammarPattern ? (
                    <>
                      {composedGrammarHero ? (
                        <div
                          className="mb-3 rounded-lg bg-green-50 px-3 py-2 dark:bg-green-900/20"
                          data-testid="popup-composed-hero"
                        >
                          <p className="mb-0.5 text-xs font-medium uppercase tracking-wide text-green-700/80 dark:text-green-400/80">
                            {copy.word.composedGrammarHero}
                          </p>
                          <p className="text-sm font-semibold text-green-800 dark:text-green-300">
                            {composedGrammarHero}
                          </p>
                        </div>
                      ) : null}
                      {showNestedWord && nestedWordConstituent && nestedWordPopup && (
                        <div
                          className={
                            composedGrammarHero
                              ? 'mb-3 rounded-lg border border-border bg-muted/40 px-3 py-2'
                              : 'mb-3 rounded-lg bg-green-50 px-3 py-2 dark:bg-green-900/20'
                          }
                          data-testid="popup-grammar-meaning"
                        >
                          <p
                            className={
                              composedGrammarHero
                                ? 'mb-0.5 text-xs font-medium uppercase tracking-wide text-muted-foreground'
                                : 'mb-0.5 text-xs font-medium uppercase tracking-wide text-green-700/80 dark:text-green-400/80'
                            }
                          >
                            {copy.word.nestedGrammarWord}
                          </p>
                          <p
                            className={
                              composedGrammarHero
                                ? 'text-sm font-semibold text-foreground'
                                : 'text-sm font-semibold text-green-800 dark:text-green-300'
                            }
                          >
                            <span data-testid="popup-nested-word">
                              {nestedWordConstituent.selection}
                            </span>
                            {(nestedWordConstituent.headline || nestedWordPopup.primaryTranslation) && (
                              <>
                                <span
                                  className={
                                    composedGrammarHero
                                      ? 'mx-1.5 font-normal text-muted-foreground'
                                      : 'mx-1.5 font-normal text-green-700/70 dark:text-green-400/70'
                                  }
                                >
                                  ·
                                </span>
                                <span>
                                  {nestedWordConstituent.headline || nestedWordPopup.primaryTranslation}
                                </span>
                              </>
                            )}
                          </p>
                        </div>
                      )}
                      {understand.literalTranslation && (
                        <p
                          className="mb-2 text-xs font-medium leading-relaxed text-foreground"
                          data-testid="popup-grammar-form-recipe"
                        >
                          {understand.literalTranslation}
                        </p>
                      )}
                      {understand.explanation && (
                        <p className="mb-3 text-sm leading-relaxed text-muted-foreground" data-testid="popup-explanation">
                          {understand.explanation}
                        </p>
                      )}
                      {understand.examples.length > 0 && (
                        <div className="mb-1 space-y-1">
                          {understand.examples.slice(0, phraseExampleLimit).map((ex, i) => (
                            <p key={i} className="text-xs italic leading-relaxed text-muted-foreground">
                              &ldquo;{ex}&rdquo;
                            </p>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                  {understand.headline && (
                    <div
                      className="mb-3 rounded-lg bg-green-50 px-3 py-2 dark:bg-green-900/20"
                      data-testid="popup-translation"
                    >
                      <p className="mb-0.5 text-xs font-medium uppercase tracking-wide text-green-700/80 dark:text-green-400/80">
                        {nativeLabel}
                      </p>
                      <p className="text-sm font-semibold text-green-800 dark:text-green-300">
                        {understand.headline}
                      </p>
                    </div>
                  )}
                  {understand.literalTranslation && (
                    <p className="mb-3 text-xs text-muted-foreground" data-testid="popup-literal">
                      <span className="font-medium">{copy.word.literalMeaning}: </span>
                      {understand.literalTranslation}
                    </p>
                  )}
                  {understand.explanation && (
                    <p className="mb-3 text-sm leading-relaxed text-foreground" data-testid="popup-explanation">
                      {understand.explanation}
                    </p>
                  )}
                  {understand.examples.length > 0 && (
                    <div className="mb-1 space-y-1">
                      {understand.examples.slice(0, phraseExampleLimit).map((ex, i) => (
                        <p key={i} className="text-xs italic leading-relaxed text-muted-foreground">
                          &ldquo;{ex}&rdquo;
                        </p>
                      ))}
                    </div>
                  )}
                  {!isDemo
                    && !isGrammarPattern
                    && understand.inCatalog !== false
                    && (understand.phraseId || understand.selection) && (
                    phraseSaved ? (
                      <div
                        className="mb-3 flex w-full items-center justify-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-400"
                        data-testid="popup-phrase-saved"
                      >
                        <Check className="h-3.5 w-3.5" />
                        {copy.word.savePhraseSaved}
                      </div>
                    ) : (
                      <div className="mb-3">
                        <button
                          type="button"
                          className={cn(
                            'flex w-full items-center justify-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-xs font-medium text-primary transition-colors hover:bg-primary/15',
                            isMobileSheet && 'min-h-11',
                          )}
                          onClick={() => addPhraseMutation.mutate()}
                          disabled={addPhraseMutation.isPending}
                          data-testid="popup-save-phrase"
                          title={copy.word.savePhraseTeaserHint}
                        >
                          <Plus className="h-3.5 w-3.5" />
                          {addPhraseMutation.isPending ? 'Saving…' : copy.word.savePhrase}
                        </button>
                        {addError ? (
                          <p className="mt-1.5 text-center text-xs text-destructive" data-testid="popup-phrase-error">
                            {addError}
                          </p>
                        ) : null}
                      </div>
                    )
                  )}
                    </>
                  )}
                    </>
                  )}

                  {showNestedWord && nestedWordConstituent && nestedWordPopup && !isGrammarPattern && (
                    <div
                      className={cn(
                        'rounded-lg border border-border bg-muted/20 p-3',
                        showPhraseBody ? 'mt-3' : 'mt-0',
                      )}
                      data-testid="popup-nested-word"
                    >
                      <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {copy.word.nestedWord}
                      </p>
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="text-base font-semibold text-foreground">
                          {nestedWordConstituent.selection}
                        </span>
                        {(nestedWordConstituent.headline
                          || nestedWordPopup.primaryTranslation) && (
                          <span className="text-sm text-muted-foreground">
                            {nestedWordConstituent.headline
                              || nestedWordPopup.primaryTranslation}
                          </span>
                        )}
                        {nestedWordPopup.cefrLevel && (
                          <CefrBadge level={nestedWordPopup.cefrLevel as CefrLevel} />
                        )}
                      </div>
                      {!isDemo && (
                        nestedWordSaved ? (
                          <div
                            className="mt-2.5 flex w-full items-center justify-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-400"
                            data-testid="popup-nested-word-saved"
                          >
                            <Check className="h-3.5 w-3.5" />
                            {copy.word.saveNestedWordSaved}
                          </div>
                        ) : (
                          <Button
                            className={cn(
                              'mt-2.5 w-full bg-secondary text-secondary-foreground hover:bg-secondary/80',
                              isMobileSheet && 'min-h-11',
                            )}
                            onClick={() => addWordMutation.mutate()}
                            disabled={addWordMutation.isPending || nestedWordPopup.enrichmentStatus === 'FAILED'}
                            data-testid="popup-add-nested-word"
                          >
                            <Plus className="mr-1.5 h-3.5 w-3.5" />
                            {addWordMutation.isPending ? 'Saving…' : copy.word.saveNestedWord}
                          </Button>
                        )
                      )}
                    </div>
                  )}
                </>
                )
              ) : popup ? (
                <>
              {popup.formInsight && (
                <div
                  className="mb-2.5 border-l-2 border-primary/40 pl-2 text-xs leading-snug text-foreground"
                  data-testid="popup-form-insight"
                >
                  <p>
                    You know{' '}
                    <span className="font-semibold">{popup.formInsight.knownLemma}</span>
                    .{' '}
                    <span className="font-semibold">{popup.formInsight.surfaceForm}</span>
                    {popup.formInsight.formLabel
                      ? ` is ${popup.formInsight.formLabel}`
                      : ''}
                    .
                  </p>
                  {isInflectedMode && popup.localizedGrammar && (
                    <p
                      className="mt-0.5 text-muted-foreground"
                      data-testid="popup-localized-grammar"
                    >
                      {popup.localizedGrammar}
                    </p>
                  )}
                </div>
              )}
              {showTranslationHero && (
                <div
                  className="mb-3 rounded-lg bg-green-50 px-3 py-2 dark:bg-green-900/20"
                  data-testid="popup-translation"
                >
                  <p className="mb-0.5 text-xs font-medium uppercase tracking-wide text-green-700/80 dark:text-green-400/80">
                    {primaryLearned ? 'Your translation' : nativeLabel}
                  </p>
                  <p className="text-sm font-semibold text-green-800 dark:text-green-300">
                    {isInflectedMode && heroTranslation
                      ? heroTranslation
                      : greenZoneTranslations.length > 1
                        ? greenZoneTranslations.join(', ')
                        : primarySense?.translation}
                  </p>
                  {alsoReadingTranslation && (
                    <p className="mt-1 text-xs text-green-700/90 dark:text-green-400/80" data-testid="popup-reading-also">
                      Also: {alsoReadingTranslation}
                    </p>
                  )}
                  {isInflectedMode && popup.teachingAlternatives && popup.teachingAlternatives.length > 0 && (
                    <p className="mt-1 text-xs text-green-700/80 dark:text-green-400/70">
                      {popup.teachingAlternatives.join(' · ')}
                    </p>
                  )}
                </div>
              )}

              {popup.popupMode === 'IDENTITY' && popup.relatedForms && popup.relatedForms.length > 0 && (
                <div className="mb-3" data-testid="popup-related-forms">
                  <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Forms
                  </p>
                  <ul className="space-y-1">
                    {popup.relatedForms.map((form) => (
                      <li
                        key={form.surfaceForm}
                        className="flex flex-wrap items-baseline gap-x-2 text-xs text-muted-foreground"
                      >
                        <span className="font-mono text-foreground">{form.surfaceForm}</span>
                        {form.teachingTranslation ? (
                          <span className="text-foreground">{form.teachingTranslation}</span>
                        ) : null}
                        {form.formDescription ? (
                          <span className="text-muted-foreground/80">{form.formDescription}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {popup.popupMode === 'IDENTITY' && popup.modalVerbLemma && !popup.inflectedFormsAvailable && (
                <p
                  className="mb-3 text-xs text-muted-foreground"
                  data-testid="popup-no-inflected-forms"
                >
                  Modal verb — no inflected forms
                </p>
              )}

              {!selectedLearned && enrichmentFailed && (
                <p className="mb-3 text-sm text-muted-foreground" data-testid="popup-no-definition">
                  Definition not available
                </p>
              )}

              {popup.localizedDefinitions.length > 0 && (
                <div className="mb-3" data-testid="popup-localized-definitions">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Meaning ({nativeLabel})
                    </p>
                    {isPolysemous && !selectedLearned && (
                      <span className="text-[11px] normal-case text-muted-foreground">
                        {copy.word.sensePickerHint}
                      </span>
                    )}
                  </div>

                  {isPolysemous ? (
                    <div
                      className="space-y-1.5"
                      role="radiogroup"
                      aria-label={copy.word.sensePickerTitle}
                    >
                      {primarySenses.map((def) => (
                        <SenseOption
                          key={def.senseKey}
                          sense={def}
                          contextPrimary={
                            popup.contextualActive === true
                            && def.senseKey === popup.rankedSenseKey
                          }
                          selected={selectedSenseKey === def.senseKey}
                          learned={isSenseLearned(def.senseKey, def.translation, learnedSenses)}
                          onSelect={() => setSelectedSenseKey(def.senseKey)}
                        />
                      ))}
                      {visibleSecondary.map((def) => (
                        <SenseOption
                          key={def.senseKey}
                          sense={def}
                          selected={selectedSenseKey === def.senseKey}
                          learned={isSenseLearned(def.senseKey, def.translation, learnedSenses)}
                          onSelect={() => setSelectedSenseKey(def.senseKey)}
                        />
                      ))}
                      {collapseSecondary && (
                        <button
                          type="button"
                          className="flex w-full items-center justify-center gap-1 pt-1 text-xs text-muted-foreground hover:text-foreground"
                          onClick={handleOtherMeaningsToggle}
                          data-testid="popup-other-meanings-toggle"
                        >
                          {otherExpanded ? (
                            <>
                              Show fewer <ChevronUp className="h-3.5 w-3.5" />
                            </>
                          ) : (
                            <>
                              {copy.word.otherMeanings} ({hiddenSecondaryCount})
                              <ChevronDown className="h-3.5 w-3.5" />
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  ) : (
                    selectedSense && <SenseRow sense={selectedSense} highlighted />
                  )}
                </div>
              )}

              {showEnglishDefinitions && (
                <div className="mb-3 space-y-2 border-t border-border pt-2" data-testid="popup-english-definitions">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    English
                  </p>
                  {popup.definitions.map((def, i) => (
                    <div key={i} className="text-sm text-muted-foreground">
                      {def.partOfSpeech && (
                        <span className="mr-1.5 text-xs italic">{def.partOfSpeech}</span>
                      )}
                      <span className={def.isPrimary ? 'font-medium' : ''}>{def.definition}</span>
                    </div>
                  ))}
                </div>
              )}

              {!popup.translationLocalized && popup.definitions.length > 0 && (
                <div className="mb-3 space-y-2">
                  {popup.definitions.map((def, i) => (
                    <div key={i} className="text-sm">
                      {def.partOfSpeech && (
                        <span className="mr-1.5 text-xs italic text-muted-foreground">
                          {def.partOfSpeech}
                        </span>
                      )}
                      <span className={def.isPrimary ? 'font-semibold text-foreground' : 'text-foreground'}>
                        {def.definition}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {popup.examples.length > 0 && (
                <div className="mb-1">
                  {popup.examples.slice(0, 1).map((ex, i) => (
                    <p key={i} className="text-xs italic leading-relaxed text-muted-foreground">
                      &ldquo;{ex}&rdquo;
                    </p>
                  ))}
                </div>
              )}
                </>
              ) : null}
            </>
          ) : null}
        </div>

        {showWordFooter && !isLoading && (
          <div
            className={cn(
              'shrink-0 border-t border-border bg-card p-3',
              isMobileSheet && 'pb-[max(0.75rem,env(safe-area-inset-bottom))]',
            )}
          >
            {isGrammarPattern ? (
              isDemo ? (
                <Button
                  className={cn('w-full', isMobileSheet && 'min-h-12')}
                  onClick={() => navigate('/register')}
                  data-testid="popup-save-grammar-episode"
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  {copy.landing.signUpToSave}
                </Button>
              ) : (
                <div className="space-y-2">
                  {grammarEpisodeSaved ? (
                    <div className="flex min-h-12 items-center justify-center gap-1.5 text-sm text-green-700 dark:text-green-400">
                      <Check className="h-4 w-4 shrink-0" />
                      <span data-testid="popup-grammar-episode-saved">
                        {copy.word.saveGrammarEpisodeMeetAgain.replace(
                          '{span}',
                          understand?.selection ?? 'this moment',
                        )}
                      </span>
                    </div>
                  ) : (
                    <Button
                      className={cn('w-full', isMobileSheet && 'min-h-12')}
                      onClick={() => saveGrammarEpisodeMutation.mutate()}
                      disabled={saveGrammarEpisodeMutation.isPending || !grammarEpisodeHero}
                      data-testid="popup-save-grammar-episode"
                    >
                      <Plus className="mr-1.5 h-3.5 w-3.5" />
                      {saveGrammarEpisodeMutation.isPending
                        ? 'Saving…'
                        : !grammarEpisodeHero
                          ? copy.word.saveGrammarEpisodeNeedMeaning
                          : copy.word.saveGrammarEpisode}
                    </Button>
                  )}
                  {canAddNestedWord ? (
                    <button
                      type="button"
                      className="flex w-full items-center justify-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                      onClick={() => addWordMutation.mutate()}
                      disabled={!canAddNestedWord}
                      data-testid="popup-add-nested-word"
                    >
                      <Plus className="h-3 w-3" />
                      {addWordMutation.isPending ? 'Saving…' : copy.word.saveNestedWord}
                    </button>
                  ) : showGrammarNestedSavedFooter ? (
                    <p
                      className="text-center text-xs text-muted-foreground"
                      data-testid="popup-grammar-saved-message"
                    >
                      {justAdded
                        ? copy.word.saveNestedWordMeetAgain.replace(
                            '{word}',
                            nestedWordConstituent?.selection ?? 'this word',
                          )
                        : copy.word.saveNestedWordSaved}
                    </p>
                  ) : null}
                  {addError ? (
                    <p className="text-center text-xs text-destructive" data-testid="popup-grammar-episode-error">
                      {addError}
                    </p>
                  ) : null}
                </div>
              )
            ) : isDemo ? (
              <Button
                className={cn('w-full', isMobileSheet && 'min-h-12')}
                onClick={() => navigate('/register')}
                data-testid="popup-add-word"
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                {copy.landing.signUpToSave}
              </Button>
            ) : showSavedFooter ? (
              <div className="flex min-h-12 items-center justify-center gap-1.5 px-2 text-center text-sm text-green-700 dark:text-green-400">
                <Check className="h-4 w-4 shrink-0" />
                <span data-testid="popup-word-saved-message">
                  {justAdded
                    ? copy.word.saveWordMeetAgain.replace(
                        '{word}',
                        understand?.selection ?? selection,
                      )
                    : copy.word.senseAlreadyLearned}
                </span>
              </div>
            ) : inVocabulary && canAdd ? (
              <Button
                className={cn('w-full', isMobileSheet && 'min-h-12')}
                onClick={() => addWordMutation.mutate()}
                disabled={addWordMutation.isPending}
                data-testid="popup-add-word"
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                {addWordMutation.isPending ? 'Saving…' : 'Add this meaning'}
              </Button>
            ) : (
              <Button
                className={cn('w-full', isMobileSheet && 'min-h-12')}
                onClick={() => addWordMutation.mutate()}
                disabled={!canAdd}
                title={enrichmentFailed ? 'No definition available to add' : undefined}
                data-testid="popup-add-word"
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                {addWordMutation.isPending ? 'Saving…' : copy.actions.saveWord}
              </Button>
            )}
            {addError ? <p className="mt-2 text-center text-xs text-destructive">{addError}</p> : null}
          </div>
        )}
      </div>
    </>
  )
}

function SenseOption({
  sense,
  selected,
  learned,
  contextPrimary,
  onSelect,
}: {
  sense: LocalizedWordDefinition
  selected: boolean
  learned: boolean
  contextPrimary?: boolean
  onSelect: () => void
}) {
  return (
    <label
      className={cn(
        'flex items-start gap-2.5 rounded-lg border px-3 py-2 transition-colors',
        selected ? 'border-primary bg-primary/5' : 'border-border',
        learned ? 'cursor-default opacity-80' : 'cursor-pointer hover:border-primary/50 hover:bg-muted/40',
      )}
      data-testid={`popup-sense-option-${sense.senseKey}`}
      data-selected={selected ? 'true' : 'false'}
    >
      <input
        type="radio"
        name="popup-sense"
        className="sr-only"
        checked={selected}
        disabled={learned}
        onChange={onSelect}
      />
      <span
        className={cn(
          'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
          selected ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40',
        )}
        aria-hidden
      >
        {selected && <Check className="h-3 w-3" />}
      </span>
      <span className="flex min-w-0 flex-1 flex-col items-start gap-1">
        {contextPrimary && (
          <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
            {copy.word.inThisSentence}
          </span>
        )}
        <SenseRow sense={sense} highlighted={selected} learned={learned} />
      </span>
    </label>
  )
}

function SenseRow({
  sense,
  highlighted,
  learned,
}: {
  sense: LocalizedWordDefinition
  highlighted?: boolean
  learned?: boolean
}) {
  return (
    <span className={`text-sm ${highlighted ? 'font-semibold text-foreground' : ''} ${learned ? 'text-muted-foreground' : 'text-foreground'}`}>
      {sense.partOfSpeech && (
        <span className="mr-1.5 text-xs italic text-muted-foreground">{sense.partOfSpeech}</span>
      )}
      {sense.translation}
      {learned && (
        <span className="ml-2 text-xs text-green-700 dark:text-green-400">✓ in vocabulary</span>
      )}
      {sense.nativeGloss && sense.nativeGloss !== sense.translation && (
        <span className="mt-0.5 block text-xs text-muted-foreground">{sense.nativeGloss}</span>
      )}
    </span>
  )
}
